import { LoadingScreen } from "@packsize/components";
import { useEffect, useRef } from "react";
import { useHref, useSearchParams } from "react-router-dom";

import { useAuthentication, useIdentityClient } from "../../hooks";

export const StartLogin = () => {
	const { client } = useIdentityClient();
	const { applicationName } = useAuthentication();
	const ssoFailureRoute = useHref("/sso-error");
	const [searchParams] = useSearchParams();
	const currentInitiationRef = useRef<{
		connection: string | null;
		applicationName: string;
		client: typeof client;
		ssoFailureRoute: string;
		active: boolean;
	} | null>(null);

	useEffect(() => {
		const connection = searchParams.get("connection");
		const previousInitiation = currentInitiationRef.current;

		if (
			previousInitiation?.connection === connection &&
			previousInitiation.applicationName === applicationName &&
			previousInitiation.client === client &&
			previousInitiation.ssoFailureRoute === ssoFailureRoute
		) {
			previousInitiation.active = true;
			return () => {
				previousInitiation.active = false;
			};
		}

		const initiation = { connection, applicationName, client, ssoFailureRoute, active: true };
		currentInitiationRef.current = initiation;

		if (connection === null) {
			window.location.replace(ssoFailureRoute);
		} else {
			const startLogin = async () => {
				try {
					const response = await client.startSsoLogin(connection, applicationName);

					if (currentInitiationRef.current !== initiation || !initiation.active) {
						return;
					}

					const redirectUrl = response?.data?.redirectUrl;

					if (response?.status !== 200 || !redirectUrl) {
						window.location.replace(ssoFailureRoute);
						return;
					}

					window.location.replace(redirectUrl);
				} catch {
					if (currentInitiationRef.current !== initiation || !initiation.active) {
						return;
					}
					window.location.replace(ssoFailureRoute);
				}
			};

			void startLogin();
		}

		return () => {
			initiation.active = false;
		};
	}, [applicationName, client, searchParams, ssoFailureRoute]);

	return <LoadingScreen />;
};
